"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-energy-cost-calculator",
  "freezer-storage-capacity-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Freezer Dry Ice Calculator",
  category: "Calculator" as const,
  mediumDescription:
    "Power outage? Calculate exactly how many pounds of dry ice you need to keep your refrigerator or freezer cold for the duration of the outage. Sized using FDA emergency-food-safety dry-ice guidance.",
  faqs: [
    { question: "How much dry ice does a freezer need?", answer: "FDA emergency-food guidance is roughly 25 lbs of dry ice per 15 cu ft of freezer space per 24 hours. A typical 15 cu ft full upright freezer needs about 25 lbs to stay frozen for one day." },
    { question: "How much dry ice does a refrigerator need?", answer: "Roughly 10 lbs per 15 cu ft per 24 hours — fridges run warmer than freezers, so they need less dry ice but should be checked more often." },
    { question: "Where should I place dry ice in the freezer?", answer: "Wrap blocks in newspaper or cardboard and place on the top shelf or on top of food. Cold air sinks, so positioning the dry ice high keeps the entire compartment cold." },
    { question: "Is dry ice safe to handle at home?", answer: "Dry ice is -109°F and causes instant frostbite — always use insulated gloves or a towel. Never store it in an airtight container (CO2 buildup ruptures the container) or use it in an unventilated room." },
    { question: "How long will food stay frozen during an outage without dry ice?", answer: "Per USDA, a full freezer holds 0°F for about 48 hours and a half-full freezer for 24 hours, as long as the door stays closed. Dry ice extends that window significantly." },
    { question: "Where can I buy dry ice during an emergency?", answer: "Most major grocery chains (Safeway, Kroger, Publix) and some hardware stores stock dry ice. Call ahead — supply gets thin during regional outages." },
  ],
  relatedTools: ["freezer-energy-cost-calculator", "freezer-storage-capacity-calculator", "energy-cost-calculator"],
}

export function EmergencyDryIceCalculatorPageClient() {
  const [cubicFeet, setCubicFeet] = useState("15")
  const [outageDuration, setOutageDuration] = useState("24")
  const [applianceType, setApplianceType] = useState("freezer")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // 1 cu ft is the smallest compact cabinet made; 50 covers the largest
    // residential freezer or side-by-side. The outage ceiling is 336 hours —
    // two weeks, past which the answer is resupply, not one dry-ice buy.
    const cuFt = readQuantity(cubicFeet, { min: 1, max: 50 })
    const hours = readQuantity(outageDuration, { min: 1, max: 336 })
    if (cuFt === null || hours === null) return null

    const poundsPerCuFtPer24Hr = applianceType === "refrigerator" ? 10 / 15 : 25 / 15
    const dryIcePounds = Math.ceil((cuFt * poundsPerCuFtPer24Hr * hours) / 24)
    const blocks = Math.ceil(dryIcePounds / 10)
    const costLow = dryIcePounds * 1
    const costHigh = dryIcePounds * 3

    return { dryIcePounds, blocks, costLow, costHigh }
  }, [cubicFeet, outageDuration, applianceType])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="How much dry ice keeps your"
      heroTitleItalic="freezer"
      heroTitlePost="cold during an outage?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "FDA emergency food guidance"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "25 lb", label: "Per 15 cu ft", note: "Freezer / 24 hr" },
            { value: "10 lb", label: "Per 15 cu ft", note: "Fridge / 24 hr" },
            { value: "48 hr", label: "Full freezer holds", note: "USDA, door shut" },
            { value: "-109°F", label: "Dry ice temp", note: "Always glove" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "freezer-dry-ice-calculator")}
      cta={{
        title: "Replace the freezer that keeps failing on you?",
        body: "Browse upright freezers with reliable warranties — sortable by capacity and energy use.",
        buttonLabel: "Browse upright freezers",
        buttonHref: "/collections/upright-freezers",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Volume.
                <br />
                Hours. Appliance.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Sized to FDA emergency dry-ice guidance — roughly 25 lb / 15 cu ft / 24 hr for freezers.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Appliance type
                  </label>
                  <select
                    value={applianceType}
                    onChange={(e) => setApplianceType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="freezer">Standalone freezer</option>
                    <option value="refrigerator">Refrigerator (with freezer)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Interior volume
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={50}
                      step="0.1"
                      value={cubicFeet}
                      onChange={(e) => setCubicFeet(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Expected outage duration
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={336}
                      step="1"
                      value={outageDuration}
                      onChange={(e) => setOutageDuration(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">hours</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Plan for the full outage, not just half of it.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Dry ice required
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.dryIcePounds ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">lbs</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `Approximately ${result.blocks} ten-pound block${result.blocks === 1 ? "" : "s"}.` : "Enter outage details to size your dry ice."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Blocks needed</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.blocks ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Est. cost</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result ? `$${result.costLow}–$${result.costHigh}` : "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Build resilience"
          title="Upright freezers that hold cold for 48 hours"
          description="A full freezer holds 0°F for about 48 hours during an outage (USDA) — capacity is your insurance."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                FDA dry ice math, <em className="italic text-[#ff9951]">simplified.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Sourced from FDA emergency food safety guidance for power outages and USDA Food Safety and Inspection Service refrigerator/freezer holding times.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Per-volume baseline", d: "Freezers: 25 lb dry ice per 15 cu ft per 24 hours. Refrigerators: 10 lb per 15 cu ft per 24 hours (FDA)." },
                { n: "02", t: "Scale by outage duration", d: "Total dry ice = (cu ft × per-cu-ft rate × outage hours) ÷ 24. Round up to the next pound." },
                { n: "03", t: "Block math", d: "Dry ice typically sells in 10 lb blocks. We round up so you never run short mid-outage." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Chest freezers — slowest to thaw"
          description="Cold air sinks and stays put — chest freezers hold temperature longer during outages than uprights."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
